import api from '@/services/api';
import type { PaginatedResponse } from '@/types';
import type {
  PatientScoreDetail,
  PatientScoreSummary,
  ScoreDistribution,
} from '@/types/patientScore';

const BASE_PATH = '/patient-scores';

/**
 * Patient score API service.
 *
 * Handles fetching patient engagement scores,
 * summaries, and distribution data.
 */
export const patientScoreService = {
  /**
   * Gets paginated patient scores with optional filters.
   */
  async getScores(
    page: number,
    limit: number,
    classification?: string,
    sortBy?: string,
  ): Promise<PaginatedResponse<PatientScoreDetail>> {
    const { data } = await api.get<PaginatedResponse<PatientScoreDetail>>(
      BASE_PATH,
      {
        params: { page, limit, classification, sortBy },
      },
    );
    return data;
  },

  /**
   * Gets detailed score for a single patient.
   */
  async getPatientScore(patientId: string): Promise<PatientScoreDetail> {
    const { data } = await api.get<PatientScoreDetail>(
      `${BASE_PATH}/${patientId}`,
    );
    return data;
  },

  /**
   * Gets score summaries for a batch of patient IDs.
   */
  async getScoreSummaries(
    patientIds: string[],
  ): Promise<PatientScoreSummary[]> {
    const { data } = await api.post<PatientScoreSummary[]>(
      `${BASE_PATH}/summaries`,
      patientIds,
    );
    return data;
  },

  /**
   * Gets score distribution across classifications.
   */
  async getDistribution(): Promise<ScoreDistribution> {
    const { data } = await api.get<ScoreDistribution>(
      `${BASE_PATH}/distribution`,
    );
    return data;
  },
};
